import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchDestinationsResults,
  fetchOutboundConnections,
  fetchOutboundDirects,
  fetchResults,
  fetchReturnConnections,
  fetchReturnDirects,
} from "../searchFlights/SearchFlightsActions";
import {
  fetchConnectionMarkersAirport,
  fetchDirectMarkers,
  fetchDirectMarkersAirport,
  fetchMarkers,
} from "./actions";
import {
  DirectionTypes,
  RMMarker,
  RMDestination,
  RMMarkerListResponse,
  RMFlightQueryResponse,
  RMRoute,
} from "../../types";
import { RootState } from "../../app/store";
import { searchFlightsSlice } from "../searchFlights/searchFlightsSlice";
import { convertLocationToMarker } from "../../utils";

export interface MarkerState {
  loading: boolean;
  selected: any;
  direct: Markers;
  connection: Markers;
  global: {
    direct: Markers;
    directAirport: Markers;
    connectionAirport: Markers;
  };
  results: {
    outboundDirect: Markers;
    returnDirect: Markers;
    outboundConnection: Markers;
    returnConnection: Markers;
    destinations: Markers;
  };
  airline: boolean;
  visibleFlightType: "direct" | "connection";
}

export interface Markers {
  markers: RMMarker[];
  routes: RMRoute[];
}

const initState = {
  loading: true,
  direct: { markers: [], routes: [] },
  connection: { markers: [], routes: [] },
  global: {
    direct: { markers: [], routes: [] },
    directAirport: { markers: [], routes: [] },
    connectionAirport: { markers: [], routes: [] },
  },
  results: {
    outboundDirect: { markers: [], routes: [] },
    returnDirect: { markers: [], routes: [] },
    outboundConnection: { markers: [], routes: [] },
    returnConnection: { markers: [], routes: [] },
    destinations: { markers: [], routes: [] },
  },
  visibleFlightType: "direct" as "direct" | "connection",
};

export const markerSlice = createSlice({
  name: "markers",
  initialState: initState as MarkerState,
  reducers: {
    setVisibleFlightType: (
      state,
      action: PayloadAction<"direct" | "connection">
    ) => {
      state.visibleFlightType = action.payload;
    },
  },
  extraReducers: {
    [searchFlightsSlice.actions.resetForm.toString()]: (state) => {
      if (state.airline) {
        state.connection = { markers: [], routes: [] };
        state.results = {
          outboundDirect: { markers: [], routes: [] },
          returnDirect: { markers: [], routes: [] },
          outboundConnection: { markers: [], routes: [] },
          returnConnection: { markers: [], routes: [] },
          destinations: { markers: [], routes: [] },
        };
        state.direct.markers = state.global.direct.markers;
        state.direct.routes = [];
      } else {
        state.direct = state.global.directAirport;
        state.connection = state.global.connectionAirport;
      }
    },
    [searchFlightsSlice.actions.resetResults.toString()]: (state, action) => {
      if (state.airline) {
        state.connection = { markers: [], routes: [] };
        state.results = {
          destinations: state.results.destinations,
          outboundDirect: { markers: [], routes: [] },
          returnDirect: { markers: [], routes: [] },
          outboundConnection: { markers: [], routes: [] },
          returnConnection: { markers: [], routes: [] },
        };
        state.direct.markers = state.global.direct.markers;
        state.direct.routes = [];
      } else {
        state.direct = state.global.directAirport;
        state.connection = state.global.connectionAirport;
      }
      if (
        action.payload === "flights" &&
        state.results.destinations.markers.length > 0
      ) {
        state.direct = state.results.destinations;
        state.connection = { markers: [], routes: [] };
      } else if (action.payload === "destinations") {
        state.results.destinations = { markers: [], routes: [] };
      }
    },
    [searchFlightsSlice.actions.updateResultsSelection.toString()]: (
      state,
      action
    ) => {
      if (action.payload.type === "direct") {
        state.direct = state.global.direct;
        state.connection = { markers: [], routes: [] };
      } else if (action.payload.type === "directAirport") {
        if (action.payload.direction === DirectionTypes.Outbound) {
          state.direct = state.results.outboundDirect;
          state.connection = { markers: [], routes: [] };
        } else if (action.payload.direction === DirectionTypes.Return) {
          state.direct = state.results.returnDirect;
          state.connection = { markers: [], routes: [] };
        }
      } else if (action.payload.type === "connectionAirport") {
        if (action.payload.direction === DirectionTypes.Outbound) {
          state.connection = state.results.outboundConnection;
          state.direct = { markers: [], routes: [] };
        } else if (action.payload.direction === DirectionTypes.Return) {
          state.connection = state.results.returnConnection;
          state.direct = { markers: [], routes: [] };
        }
      }
    },
    [fetchMarkers.pending.toString()]: (state) => {
      state.loading = true;
      state.direct = { markers: [], routes: [] };
      state.connection = { markers: [], routes: [] };
    },
    [fetchMarkers.rejected.toString()]: (state) => {
      state.loading = false;
    },
    [fetchMarkers.fulfilled.toString()]: (state) => {
      state.loading = false;
      state.direct.markers = state.global.directAirport.markers;
      state.connection.markers = state.global.connectionAirport.markers;
    },
    [fetchDirectMarkers.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMMarkerListResponse>
    ) => {
      state.loading = false;
      state.airline = true;
      state.global.direct.markers = action.payload.results;
      state.direct.markers = action.payload.results;
    },
    [fetchDirectMarkersAirport.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMMarkerListResponse>
    ) => {
      state.global.directAirport.markers = action.payload.results;
      if (
        state.global[action.payload.requestDetail.originCriteria] === undefined
      ) {
        state.global[action.payload.requestDetail.originCriteria] = {
          direct: { markers: [], routes: [] },
          connection: { markers: [], routes: [] },
        };
      }
      state.global[action.payload.requestDetail.originCriteria].direct.markers =
        action.payload.results;
    },
    [fetchConnectionMarkersAirport.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMMarkerListResponse>
    ) => {
      state.global.connectionAirport.markers = action.payload.results;
      if (
        state.global[action.payload.requestDetail.originCriteria] === undefined
      ) {
        state.global[action.payload.requestDetail.originCriteria] = {
          direct: { markers: [], routes: [] },
          connection: { markers: [], routes: [] },
        };
      }
      state.global[
        action.payload.requestDetail.originCriteria
      ].connection.markers = action.payload.results;
    },
    [fetchOutboundDirects.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.outboundDirect = {
        markers: filterMarkers(action.payload),
        routes: filterRoutes(action.payload),
      };
      state.direct = state.results.outboundDirect;
      state.connection = { markers: [], routes: [] };
    },
    [fetchOutboundConnections.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.outboundConnection = {
        markers: action.payload.markers,
        routes: action.payload.routes,
      };
    },
    [fetchReturnDirects.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.returnDirect = {
        markers: filterMarkers(action.payload),
        routes: filterRoutes(action.payload),
      };
    },
    [fetchReturnConnections.fulfilled.toString()]: (
      state,
      action: PayloadAction<RMFlightQueryResponse>
    ) => {
      state.results.returnConnection = {
        markers: action.payload.markers,
        routes: action.payload.routes,
      };
    },
    [fetchResults.pending.toString()]: (state) => {
      state.direct = { markers: [], routes: [] };
      state.connection = { markers: [], routes: [] };
    },
    [fetchResults.fulfilled.toString()]: (state, action) => {
      // Check if flight type toggle feature is enabled
      const { enableFlightTypeToggle, visibleFlightType } =
        action.payload || {};

      if (enableFlightTypeToggle) {
        // If toggle feature is enabled, set markers/routes based on visible flight type
        if (visibleFlightType === "direct") {
          state.direct = state.results.outboundDirect;
          state.connection = { markers: [], routes: [] };
        } else if (visibleFlightType === "connection") {
          state.connection = state.results.outboundConnection;
          state.direct = { markers: [], routes: [] };
        }
      } else {
        // Default behavior: if no direct flights but has connections, show connections
        if (
          state.direct.markers.length === 0 &&
          state.results.outboundConnection.markers.length > 0
        ) {
          state.connection = state.results.outboundConnection;
        }
      }
    },
    [fetchDestinationsResults.pending.toString()]: (state) => {
      state.results.destinations = { markers: [], routes: [] };
      state.direct = { markers: [], routes: [] };
      state.connection = { markers: [], routes: [] };
    },
    [fetchDestinationsResults.fulfilled.toString()]: (state, action) => {
      state.results.destinations.markers = action.payload.results.map(
        (destination: RMDestination) => {
          return convertLocationToMarker(destination.location);
        }
      );
      state.direct.markers = state.results.destinations.markers;
    },
  },
});

export const selectLoadingMarkers = (state: RootState) => {
  return state.marker.loading;
};

export const selectDirectMarkers = (state: RootState) => {
  return state.marker.direct;
};

export const selectConnectionMarkers = (state: RootState) => {
  return state.marker.connection;
};

export const selectVisibleFlightType = (state: RootState) => {
  return state.marker.visibleFlightType;
};

export const { setVisibleFlightType } = markerSlice.actions;

function filterMarkers(payload: RMFlightQueryResponse) {
  return payload.markers.filter((marker) => {
    if (payload.requestDetail.nonStopDirect) {
      return (
        marker.code === payload.requestDetail.originCriteria ||
        marker.code === payload.requestDetail.destinationCriteria
      );
    } else {
      return true;
    }
  });
}

function filterRoutes(payload: RMFlightQueryResponse) {
  return payload.routes.filter((route) => {
    if (payload.requestDetail.nonStopDirect) {
      return route.key.split("-").length === 2;
    } else {
      return true;
    }
  });
}

export default markerSlice.reducer;
